/**
 * StickyOperatorLoginButton — Pulsante fisso in basso a sinistra
 * Click apre direttamente la selezione operatore + QR senza interrompere nulla
 */
import { useState, useCallback } from 'react';
import { QrCode, Loader2, X, User } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../auth/AuthProvider';

// generate-login-token richiede il JWT dal 2026-08-03: senza header il pannello
// smetterebbe di generare QR e i telefoni non entrerebbero piu'.
const authHeader = (): Record<string, string> => {
  try { const t = localStorage.getItem('accessToken'); return t ? { Authorization: `Bearer ${t}` } : {}; } catch { return {}; }
};

interface Operator {
  id: number;
  username: string;
  displayName: string;
  role: string;
  active: boolean;
  sede_assegnata?: string | null;
}

export default function StickyOperatorLoginButton() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState<{ qrCode: string; qrUrl?: string; operatorName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadOperators = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/mobile-operators/admin/operators?active=true');
      const data = await res.json();
      if (data.success) setOperators(data.operators || []);
      else setError(data.error);
    } catch { setError('Errore di rete'); }
    finally { setLoading(false); }
  }, []);

  const generateQR = useCallback(async (operator: Operator) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/mobile-operators/auth/generate-login-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          operatorUsername: operator.username,
          desktopUserId: user?.id,
          qrUrlBase: window.location.origin
        }),
      });
      const data = await res.json();
      if (data.success) {
        setQrData({ qrCode: data.qrDataUrl, qrUrl: data.qrUrl, operatorName: operator.displayName });
      } else setError(data.error);
    } catch { setError('Errore generazione QR'); }
    finally { setLoading(false); }
  }, [user]);

  const handleOpen = () => {
    setIsOpen(true);
    setQrData(null);
    loadOperators();
  };

  const handleClose = () => {
    setIsOpen(false);
    setQrData(null);
    setError(null);
  };

  if (!user) return null;

  return (
    <>
      <button
        onClick={handleOpen}
        className="fixed bottom-6 left-6 z-[99997] w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        title="Login Operatori Smartphone"
      >
        <QrCode className="w-5 h-5 group-hover:scale-110 transition-transform" />
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[99998] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
          <div className="relative bg-slate-800 border border-cyan-500/30 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-b border-cyan-500/30 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-cyan-400" />
                <h3 className="text-lg font-bold text-white">Login Operatore</h3>
              </div>
              <button onClick={handleClose} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {error && <div className="mb-3 p-2 bg-red-500/20 border border-red-500/30 rounded text-red-300 text-sm">{error}</div>}

              {loading && !qrData && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                </div>
              )}

              {!qrData && !loading && operators.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-400 mb-3">Seleziona operatore:</p>
                  {operators.map(op => (
                    <button
                      key={op.id}
                      onClick={() => generateQR(op)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 hover:border-cyan-500/50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{op.displayName}</p>
                        <p className="text-gray-400 text-xs">{op.role} • {op.username}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {qrData && (
                <div className="text-center">
                  <p className="text-cyan-400 font-medium mb-3">{qrData.operatorName}</p>
                  <div className="bg-white rounded-xl p-4 inline-block">
                    {/* 440px in entrambi i rami. Misurato sul campo: sotto i ~300px una
                        webcam a fuoco fisso (Logitech C310) non decodifica il QR di login,
                        che e' denso perche' contiene l'URL col token. Erano 200, cioe'
                        illeggibile da webcam (2026-08-17). */}
                    {qrData.qrUrl ? (
                      <QRCodeSVG value={qrData.qrUrl} size={520} level="H" includeMargin />
                    ) : qrData.qrCode.startsWith('data:') ? (
                      <img src={qrData.qrCode} alt="QR Login" width={520} height={520} />
                    ) : (
                      <QRCodeSVG value={qrData.qrCode} size={520} level="H" includeMargin />
                    )}
                  </div>
                  <p className="text-gray-400 text-xs mt-3">Scansiona con la webapp operatore</p>
                  <button
                    onClick={() => setQrData(null)}
                    className="mt-3 text-sm text-cyan-400 hover:text-cyan-300"
                  >
                    ← Cambia operatore
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
